import React, { useContext, useEffect } from 'react';
import { message, Modal, Select } from 'antd';
import { connect } from 'react-redux';
import { getAgents } from 'actions/landlordPropertiesActions';
import { CurrentApplicationContext } from '../../ApplicationOverview';
import { assignAgent, resetApplicationStatuses } from '../../store/actions';
import { getTranslatedString } from 'helpers/i18n';
import { ApplicationUIContext, Form } from '@yuhuinc/ui-library';
import { useQueryClient } from '@tanstack/react-query';
const { Option } = Select;

const AssignedAgent = ({
  getAgents,
  resetApplicationStatuses,
  availableAgents = [],
  assignAgent,
  assignAgentSuccess,
  assignAgentFailure,
}) => {
  const application = useContext(CurrentApplicationContext);
  const { isMobile } = useContext(ApplicationUIContext);
  const [assignedAgentForm] = Form.useForm();
  const queryClient = useQueryClient();

  useEffect(() => {
    // Fetch it only once
    getAgents({ accessible_property_id: application?.property?.id });
  }, []);

  useEffect(() => {
    if (assignAgentSuccess) {
      message
        .success(
          getTranslatedString(
            'landlordPortal.applicationManagement.applicationOverview.overview.assignedAgent.agentAssigned'
          )
        )
        .then(() => {
          queryClient.invalidateQueries({ queryKey: ['applications'] });
          resetApplicationStatuses();
        });
    }

    if (assignAgentFailure) {
      message.error('Failed to assign agent').then(resetApplicationStatuses());
    }
  }, [assignAgentSuccess, assignAgentFailure]);

  const onSubmitAssignedAgent = (agentId) => {
    assignAgent(application?.id, agentId);
  };

  const resetToPreviousValue = () => {
    const selectedAgentInTheBackend = application?.assigned_agent?.id || null;
    assignedAgentForm.setFieldsValue({ assigned_agent: selectedAgentInTheBackend });
  };

  const onChange = (value, option) => {
    const increaseButtonSizeOnMobile = { size: isMobile ? 'large' : 'default' };

    Modal.confirm({
      title: getTranslatedString(
        'landlordPortal.applicationManagement.applicationOverview.overview.assignedAgent.assignLeasingAgent'
      ),
      content: getTranslatedString(
        'landlordPortal.applicationManagement.applicationOverview.overview.assignedAgent.confirmMessage',
        { agentName: option?.children }
      ),
      onOk: () => onSubmitAssignedAgent(value),
      onCancel: resetToPreviousValue, // resets to previous value if user cancels
      okText: getTranslatedString(
        'landlordPortal.applicationManagement.applicationOverview.overview.assignedAgent.confirm'
      ),
      cancelText: getTranslatedString('cancel'),
      okButtonProps: increaseButtonSizeOnMobile,
      cancelButtonProps: increaseButtonSizeOnMobile,
      bodyStyle: { fontSize: isMobile ? 16 : 14 },
    });
  };

  return (
    <Form
      layout="vertical"
      form={assignedAgentForm}
      initialValues={{ assigned_agent: application?.assigned_agent.id }}
    >
      <Form.Item
        label={getTranslatedString(
          'landlordPortal.applicationManagement.applicationOverview.overview.assignedAgent'
        )}
        name={'assigned_agent'}
      >
        <Select
          showSearch
          placeholder={getTranslatedString(
            'landlordPortal.applicationManagement.applicationOverview.overview.assignedAgent.selectAnAgent'
          )}
          onChange={onChange}
          optionFilterProp="children"
          filterOption={(input, option) =>
            option.children.toLowerCase().includes(input.toLowerCase())
          }
          style={{ maxWidth: 456 }}
          size={isMobile ? 'large' : 'default'}
        >
          {availableAgents.map((agent) => (
            <Option key={agent.value} value={agent.value}>
              {agent.label}
            </Option>
          ))}
        </Select>
      </Form.Item>
    </Form>
  );
};

const mapStoreToProps = (store) => ({
  availableAgents: store.landlordProperties.agents,
  assignAgentSuccess: store.landlord.applicationManagement.assignAgentSuccess,
  assignAgentFailure: store.landlord.applicationManagement.assignAgentFailure,
});

const mapDispatchToProps = {
  getAgents,
  resetApplicationStatuses,
  assignAgent,
};

export default connect(mapStoreToProps, mapDispatchToProps)(AssignedAgent);
